import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import axiosInstance, {
  checkTokenValidity,
  CheckUserExpiry,
  getUserDetails,
  canEditSamplingFishData,
  handleLegacySessionExpired,
} from "../../components/content/axiosConfig";
import { resolveScopedFisherID } from "../../utils/pondListForUser";
import Preloader from "../../components/custom/preLoader";
import showErrorToast, { showSuccessToast } from "../../components/toast/toast";
import PondDetailStandardWeightUnits from "../../components/custom/PondDetailStandardWeightUnits";
import ClockTimeInput from "../../components/custom/ClockTimeInput";
import {
  POND_UPLOAD_COLUMNS,
  POND_UPLOAD_PREVIEW_ROW_LIMIT,
  SAMPLING_METHOD_OPTIONS,
  CAPTURE_METHOD_OPTIONS,
  isAnglingSamplingMethod,
  requiresEffortUnits,
  parseWorkbookFile,
  normalizeUploadRows,
  buildSpeciesLookup,
  buildUploadPreview,
  buildImportPayload,
  downloadPondUploadTemplate,
  flattenPreviewFishRows,
} from "../../utils/pondSamplingUpload";
import {
  fromClockTimeValue,
  isValidClockTimeInput,
} from "../../utils/samplingCpueUtils";
import {
  STANDARD_WEIGHT_SYSTEMS,
  inferStandardWeightSystem,
  resolveStandardWeightUnits,
} from "../../utils/standardWeightUnits";
import { displayMethodLabel } from "../../utils/samplingDisplayLabels";

const WF_INPUT = {
  border: "1px solid var(--wf-input-border, #dee2e6)",
  borderRadius: "8px",
  height: "3.5rem",
  minHeight: "3.5rem",
  fontSize: "0.95rem",
  fontWeight: 500,
  backgroundColor: "var(--wf-input-bg, #fafafa)",
  color: "var(--wf-input-text, inherit)",
};

const WF_LABEL = {
  color: "var(--wf-label, #6c757d)",
  fontSize: "0.85rem",
};

const STEPS = [
  { id: 1, label: "Settings" },
  { id: 2, label: "Import" },
  { id: 3, label: "Done" },
];

const DEFAULT_START = "8:00 AM";
const DEFAULT_STOP = "12:00 PM";

/**
 * Pond-level multi-date sampling upload — same Excel UX as session fish import.
 */
const PondSamplingUpload = () => {
  const { pondId } = useParams();
  const history = useHistory();
  const userDetails = getUserDetails();
  const canEdit = canEditSamplingFishData(userDetails);
  const fileInputRef = useRef(null);

  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isCommitting, setIsCommitting] = useState(false);
  const [isParsingFile, setIsParsingFile] = useState(false);
  const [pond, setPond] = useState(null);
  const [lengthUnits, setLengthUnits] = useState([]);
  const [weightUnits, setWeightUnits] = useState([]);
  const [fishTypes, setFishTypes] = useState([]);
  const [existingSessions, setExistingSessions] = useState([]);
  const [unitsError, setUnitsError] = useState("");
  const [dragOver, setDragOver] = useState(false);

  const [settings, setSettings] = useState({
    lengthUnitId: "",
    weightUnitId: "",
    samplingMethod: "Fishing",
    defaultCaptureMethod: "Angling",
    timeStarted: DEFAULT_START,
    timeStopped: DEFAULT_STOP,
    numberOfAnglers: 1,
    defaultAnglerLabel: "Upload",
  });

  const [importFile, setImportFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [commitResult, setCommitResult] = useState(null);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });

  const fisherID = resolveScopedFisherID(userDetails, pond);

  const lengthUnitName = useMemo(() => {
    const u = lengthUnits.find((x) => String(x.id) === String(settings.lengthUnitId));
    return u?.Name || "";
  }, [lengthUnits, settings.lengthUnitId]);

  const weightUnitName = useMemo(() => {
    const u = weightUnits.find((x) => String(x.id) === String(settings.weightUnitId));
    return u?.Name || "";
  }, [weightUnits, settings.weightUnitId]);

  const speciesLookup = useMemo(() => buildSpeciesLookup(fishTypes), [fishTypes]);

  const settingsValid =
    Boolean(settings.lengthUnitId) &&
    Boolean(settings.weightUnitId) &&
    isValidClockTimeInput(settings.timeStarted) &&
    isValidClockTimeInput(settings.timeStopped);

  const previewFishRows = useMemo(
    () => flattenPreviewFishRows(preview, POND_UPLOAD_PREVIEW_ROW_LIMIT),
    [preview]
  );

  const requiredColumnLabels = POND_UPLOAD_COLUMNS.filter((c) => c.required)
    .map((c) => c.header)
    .join(", ");

  const loadPage = useCallback(async () => {
    try {
      setIsLoading(true);
      const [pondRes, lengthRes, weightRes, fishRes, sessionsRes] = await Promise.all([
        axiosInstance.get(`/api/Pond/GetPondByID`, { params: { id: pondId } }),
        axiosInstance.get(`/api/LengthUnit/GetAll`),
        axiosInstance.get(`/api/WeightUnit/GetAll`),
        axiosInstance.get(`/api/FishTypes/GetAllFishTypes`),
        axiosInstance.get(`/api/PondDetail/GetAllDetailsByPondId/${pondId}`),
      ]);

      const pondData = pondRes.data;
      setPond(pondData);
      const lengths = Array.isArray(lengthRes.data)
        ? lengthRes.data
        : lengthRes.data?.data || [];
      const weights = Array.isArray(weightRes.data)
        ? weightRes.data
        : weightRes.data?.data || [];
      const fishes = Array.isArray(fishRes.data)
        ? fishRes.data
        : fishRes.data?.data || [];
      setLengthUnits(lengths);
      setWeightUnits(weights);
      setFishTypes(fishes);
      setExistingSessions(Array.isArray(sessionsRes.data) ? sessionsRes.data : []);

      const pondLength = pondData?.Length_Unit ?? pondData?.LengthUnitID ?? "";
      const pondWeight = pondData?.Weight_Unit ?? pondData?.WeightUnitID ?? "";

      let system = inferStandardWeightSystem(pondLength, pondWeight, lengths, weights);
      if (!system) {
        const lenName = String(
          lengths.find((u) => String(u.id) === String(pondLength))?.Name || ""
        ).toLowerCase();
        if (lenName.includes("mm") || lenName.includes("millimeter")) {
          system = STANDARD_WEIGHT_SYSTEMS.METRIC;
        } else {
          system = STANDARD_WEIGHT_SYSTEMS.ENGLISH;
        }
      }

      const resolved =
        resolveStandardWeightUnits(system, lengths, weights) ||
        resolveStandardWeightUnits(STANDARD_WEIGHT_SYSTEMS.ENGLISH, lengths, weights) ||
        resolveStandardWeightUnits(STANDARD_WEIGHT_SYSTEMS.METRIC, lengths, weights);

      setSettings((prev) => ({
        ...prev,
        lengthUnitId: resolved ? String(resolved.lengthUnitId) : "",
        weightUnitId: resolved ? String(resolved.weightUnitId) : "",
        timeStarted: DEFAULT_START,
        timeStopped: DEFAULT_STOP,
      }));
    } catch (error) {
      if (error.response?.status === 401) {
        const auth = handleLegacySessionExpired(error);
        showErrorToast(auth.message);
      } else {
        showErrorToast(error.response?.data?.message || "Failed to load pond");
      }
    } finally {
      setIsLoading(false);
    }
  }, [pondId]);

  useEffect(() => {
    checkTokenValidity();
    CheckUserExpiry();
    loadPage();
  }, [loadPage]);

  useEffect(() => {
    if (isAnglingSamplingMethod(settings.samplingMethod)) {
      setSettings((prev) => ({
        ...prev,
        defaultCaptureMethod: "Angling",
      }));
    } else if (requiresEffortUnits(settings.samplingMethod)) {
      const map = {
        Electrofishing: "Electrofishing",
        "Fyke Net": "Fyke Net",
        Gillnet: "Gillnet",
        Seine: "Seine",
      };
      setSettings((prev) => ({
        ...prev,
        defaultCaptureMethod: map[prev.samplingMethod] || prev.defaultCaptureMethod,
      }));
    }
  }, [settings.samplingMethod]);

  const onSettingsChange = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleUnitsChange = ({ weightUnitId, lengthUnitId }) => {
    setUnitsError("");
    setSettings((prev) => ({
      ...prev,
      weightUnitId: weightUnitId != null && weightUnitId !== "" ? String(weightUnitId) : "",
      lengthUnitId: lengthUnitId != null && lengthUnitId !== "" ? String(lengthUnitId) : "",
    }));
  };

  const clearImportState = () => {
    setImportFile(null);
    setPreview(null);
    setIsParsingFile(false);
    setDragOver(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      showErrorToast("Please upload an Excel file (.xlsx or .xls)");
      return;
    }

    setImportFile(file);
    setPreview(null);
    setIsParsingFile(true);

    try {
      const buffer = await file.arrayBuffer();
      const { rows } = parseWorkbookFile(buffer, file.name);
      const normalized = normalizeUploadRows(rows);
      const built = buildUploadPreview({
        normalizedRows: normalized,
        speciesLookup,
        lengthUnitName,
        existingSessions,
        pondName: pond?.Name || "Pond",
      });
      setPreview(built);
      if (!built.ok) {
        showErrorToast(built.errors[0]?.message || "Fix file errors before importing");
      }
    } catch (err) {
      console.error(err);
      setImportFile(null);
      setPreview(null);
      showErrorToast(err.message || "Error reading file. Please check the format.");
    } finally {
      setIsParsingFile(false);
    }
  };

  const handleCommit = async () => {
    if (!preview?.ok || !canEdit || !importFile) return;
    if (!settings.lengthUnitId || !settings.weightUnitId) {
      showErrorToast("Select length and weight units");
      setUnitsError("Choose a standard weight system");
      return;
    }

    const importSettings = {
      ...settings,
      timeStarted: fromClockTimeValue(settings.timeStarted) || "8:00:00",
      timeStopped: fromClockTimeValue(settings.timeStopped) || "12:00:00",
    };
    const totalFish = (preview.sessions || []).reduce(
      (n, session) => n + (session.samples?.length || 0),
      0
    );
    const notifyEmail = userDetails?.Email || userDetails?.email || null;
    const notifyName = userDetails?.FullName || userDetails?.Name || null;

    setIsCommitting(true);
    setImportProgress({ current: 0, total: totalFish });

    try {
      const payload = {
        ...buildImportPayload({
          pondId,
          fisherId: fisherID || pond?.FisherID,
          settings: importSettings,
          previewSessions: preview.sessions || [],
        }),
        FileName: importFile.name,
        NotifyEmail: notifyEmail,
        NotifyName: notifyName,
      };

      const res = await axiosInstance.post(
        `/api/PondDetail/ImportSamplingByDatesAsync`,
        payload,
        { timeout: 120000 }
      );

      const message =
        res.data?.message ||
        `Import queued for ${totalFish} fish. We will email you when it finishes.`;
      setCommitResult({
        queued: true,
        message,
        jobId: res.data?.jobId,
        notifyEmail: res.data?.notifyEmail || notifyEmail,
        samplesCreated: totalFish,
        sessions: (preview.sessions || []).map((s) => ({
          pondDID: s.existingPondDID || `pending-${s.dateIso}`,
          name: s.name,
          date: s.dateIso,
          sampleCount: s.samples?.length || s.rowCount || 0,
          created: !s.isAppend,
          pending: true,
        })),
      });
      showSuccessToast(message);
      setStep(3);
    } catch (error) {
      if (error.response?.status === 401) {
        const auth = handleLegacySessionExpired(error);
        showErrorToast(auth.message);
      } else if (error.response?.status === 413) {
        showErrorToast("Upload was too large for the server. Try a smaller file.");
      } else {
        const data = error.response?.data;
        const serverMessage =
          (data && typeof data === "object" && data.message) ||
          (typeof data === "string" && data.trim()) ||
          "";
        showErrorToast(serverMessage || error.message || "Import failed");
      }
    } finally {
      setIsCommitting(false);
    }
  };

  if (isLoading) return <Preloader />;

  if (!canEdit) {
    return (
      <div className="donezo-page donezo-list-page donezo-upload-page">
        <div className="container-fluid px-3 px-md-4 py-4" style={{ maxWidth: 720 }}>
          <div className="donezo-upload-notice donezo-upload-notice--warn">
            You do not have permission to upload sampling data for this pond.
          </div>
          <Link to={`/pondDetailViaPond/${pondId}`} className="donezo-orders-toolbar__ghost">
            Back to sampling list
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="donezo-page donezo-list-page donezo-workflow-page donezo-upload-page">
      <div className="container-fluid px-3 px-md-4 py-3 py-md-4">
        <nav aria-label="breadcrumb" className="mb-2">
          <ol className="breadcrumb mb-0 small">
            <li className="breadcrumb-item">
              <Link to="/dashboard" className="text-decoration-none text-muted">
                Home
              </Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/fishingData" className="text-decoration-none text-muted">
                Fishing Data
              </Link>
            </li>
            <li className="breadcrumb-item">
              <Link to={`/pondDetailViaPond/${pondId}`} className="text-decoration-none text-muted">
                Sampling Data
              </Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Upload
            </li>
          </ol>
        </nav>

        <div className="donezo-orders-toolbar donezo-upload-toolbar">
          <div className="donezo-upload-toolbar__intro">
            <h1 className="donezo-orders-toolbar__title">Import from Excel</h1>
            <p className="donezo-upload-toolbar__subtitle">
              {pond?.Name ? `${pond.Name} · ` : ""}
              Multi-date upload — each date becomes a sampling session
            </p>
          </div>
        </div>

        <div className="donezo-orders-panel donezo-upload-stepper mb-3">
          <div className="donezo-upload-stepper__inner">
            {STEPS.map((s, idx) => {
              const active = step === s.id;
              const done = step > s.id;
              return (
                <div
                  key={s.id}
                  className={`donezo-upload-stepper__item${active ? " is-active" : ""}${
                    done ? " is-done" : ""
                  }`}
                >
                  <span className="donezo-upload-stepper__badge" aria-hidden>
                    {done ? <i className="fa fa-check" /> : s.id}
                  </span>
                  <span className="donezo-upload-stepper__label d-none d-sm-inline">{s.label}</span>
                  {idx < STEPS.length - 1 && <span className="donezo-upload-stepper__line" aria-hidden />}
                </div>
              );
            })}
          </div>
        </div>

        {step === 1 && (
          <>
            <div className="donezo-orders-panel donezo-upload-section mb-3">
              <div className="donezo-upload-section__header">
                <i className="fa fa-balance-scale" aria-hidden />
                <h2 className="donezo-upload-section__title">Units</h2>
              </div>
              <div className="donezo-upload-section__body">
                <p className="donezo-upload-section__hint">
                  Units apply to every date in the file and must match the numbers in your Excel file.
                </p>
                <PondDetailStandardWeightUnits
                  weightUnitId={settings.weightUnitId}
                  lengthUnitId={settings.lengthUnitId}
                  onUnitsChange={handleUnitsChange}
                  unitsError={unitsError}
                  idPrefix="pondUpload"
                />
              </div>
            </div>

            <div className="donezo-orders-panel donezo-upload-section mb-3">
              <div className="donezo-upload-section__header">
                <i className="fa fa-clock" aria-hidden />
                <h2 className="donezo-upload-section__title">Session defaults</h2>
              </div>
              <div className="donezo-upload-section__body">
                <div className="row g-2">
                  <div className="col-12 col-md-6">
                    <div className="form-floating mb-2">
                      <select
                        className="form-control"
                        id="pondUploadSamplingMethod"
                        style={WF_INPUT}
                        value={settings.samplingMethod}
                        onChange={(e) => onSettingsChange("samplingMethod", e.target.value)}
                      >
                        {SAMPLING_METHOD_OPTIONS.map((m) => (
                          <option key={m} value={m}>
                            {displayMethodLabel(m) || m}
                          </option>
                        ))}
                      </select>
                      <label htmlFor="pondUploadSamplingMethod" style={WF_LABEL}>
                        Sampling method
                      </label>
                    </div>
                  </div>

                  <div className="col-12 col-md-6">
                    <div className="form-floating mb-2">
                      <select
                        className="form-control"
                        id="pondUploadCaptureMethod"
                        style={WF_INPUT}
                        value={settings.defaultCaptureMethod}
                        onChange={(e) => onSettingsChange("defaultCaptureMethod", e.target.value)}
                      >
                        {CAPTURE_METHOD_OPTIONS.map((m) => (
                          <option key={m} value={m}>
                            {displayMethodLabel(m) || m}
                          </option>
                        ))}
                      </select>
                      <label htmlFor="pondUploadCaptureMethod" style={WF_LABEL}>
                        Default capture method
                      </label>
                    </div>
                  </div>

                  <div className="col-12 col-md-6">
                    <div className="form-floating mb-2">
                      <ClockTimeInput
                        id="pondUploadTimeStarted"
                        name="timeStarted"
                        className="form-control"
                        style={WF_INPUT}
                        value={settings.timeStarted}
                        onChange={(e) => onSettingsChange("timeStarted", e.target.value)}
                        placeholder="800 AM"
                      />
                      <label htmlFor="pondUploadTimeStarted" style={WF_LABEL}>
                        Default start time
                      </label>
                    </div>
                  </div>

                  <div className="col-12 col-md-6">
                    <div className="form-floating mb-2">
                      <ClockTimeInput
                        id="pondUploadTimeStopped"
                        name="timeStopped"
                        className="form-control"
                        style={WF_INPUT}
                        value={settings.timeStopped}
                        onChange={(e) => onSettingsChange("timeStopped", e.target.value)}
                        placeholder="1200 PM"
                      />
                      <label htmlFor="pondUploadTimeStopped" style={WF_LABEL}>
                        Default stop time
                      </label>
                    </div>
                  </div>

                  {isAnglingSamplingMethod(settings.samplingMethod) && (
                    <>
                      <div className="col-12 col-md-6">
                        <div className="form-floating mb-2">
                          <input
                            type="number"
                            min={1}
                            className="form-control"
                            id="pondUploadAnglers"
                            style={WF_INPUT}
                            value={settings.numberOfAnglers}
                            onChange={(e) => onSettingsChange("numberOfAnglers", e.target.value)}
                          />
                          <label htmlFor="pondUploadAnglers" style={WF_LABEL}>
                            Number of anglers
                          </label>
                        </div>
                      </div>
                      <div className="col-12 col-md-6">
                        <div className="form-floating mb-2">
                          <input
                            type="text"
                            className="form-control"
                            id="pondUploadAnglerLabel"
                            style={WF_INPUT}
                            value={settings.defaultAnglerLabel}
                            onChange={(e) => onSettingsChange("defaultAnglerLabel", e.target.value)}
                          />
                          <label htmlFor="pondUploadAnglerLabel" style={WF_LABEL}>
                            Default angler label
                          </label>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-end">
              <button
                type="button"
                className="donezo-orders-toolbar__primary"
                disabled={!settingsValid}
                onClick={() => {
                  if (!settings.lengthUnitId || !settings.weightUnitId) {
                    setUnitsError("Choose a standard weight system");
                    return;
                  }
                  clearImportState();
                  setStep(2);
                }}
              >
                Next: Import Excel
                <i className="fa fa-arrow-right ms-2" aria-hidden />
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <div className="position-relative">
            {isCommitting && (
              <div className="donezo-upload-overlay">
                <div className="donezo-upload-overlay__card">
                  <i className="fas fa-cloud-upload-alt donezo-upload-overlay__icon" aria-hidden />
                  <h4 className="donezo-upload-overlay__title">Queuing import…</h4>
                  <div className="progress donezo-upload-overlay__progress">
                    <div
                      className="progress-bar progress-bar-striped progress-bar-animated"
                      role="progressbar"
                      style={{ width: "100%" }}
                    />
                  </div>
                  <p className="donezo-upload-overlay__text">
                    Uploading {importProgress.total || "your"} fish samples. You can leave this
                    page — we will email you when the import finishes.
                  </p>
                </div>
              </div>
            )}

            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <div className="donezo-orders-panel donezo-upload-section h-100">
                  <div className="donezo-upload-section__header">
                    <i className="fas fa-download" aria-hidden />
                    <h2 className="donezo-upload-section__title">Get template</h2>
                  </div>
                  <div className="donezo-upload-section__body">
                    <p className="donezo-upload-section__hint">
                      Required columns: <strong>{requiredColumnLabels}</strong>
                      {" "}(+ optional Tag Number, Harvested).
                    </p>
                    <button
                      type="button"
                      className="donezo-orders-toolbar__ghost w-100 justify-content-center"
                      onClick={() =>
                        downloadPondUploadTemplate({
                          fishTypes,
                          pondName: pond?.Name || "",
                        })
                      }
                    >
                      <i className="fas fa-file-download" aria-hidden />
                      Download template
                    </button>
                  </div>
                </div>
              </div>

              <div className="col-md-6">
                <div className="donezo-orders-panel donezo-upload-section h-100">
                  <div className="donezo-upload-section__header">
                    <i className="fas fa-upload" aria-hidden />
                    <h2 className="donezo-upload-section__title">Upload data</h2>
                  </div>
                  <div className="donezo-upload-section__body">
                    <p className="donezo-upload-section__hint">
                      Select your filled Excel file to import.
                    </p>
                    <div
                      className={`donezo-upload-dropzone${dragOver ? " is-dragover" : ""}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        if (!isParsingFile && !isCommitting) fileInputRef.current?.click();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          if (!isParsingFile && !isCommitting) fileInputRef.current?.click();
                        }
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDragOver(true);
                      }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={(e) => {
                        e.preventDefault();
                        setDragOver(false);
                        if (!isParsingFile && !isCommitting) {
                          handleFile(e.dataTransfer.files?.[0]);
                        }
                      }}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        className="d-none"
                        id="pondExcelFileInput"
                        accept=".xlsx,.xls"
                        disabled={isParsingFile || isCommitting}
                        onChange={(e) => handleFile(e.target.files?.[0])}
                      />
                      {isParsingFile ? (
                        <>
                          <div className="spinner-border spinner-border-sm text-success mb-2" role="status" />
                          <p className="mb-0">Reading Excel file…</p>
                        </>
                      ) : (
                        <>
                          <i className="fas fa-cloud-upload-alt donezo-upload-dropzone__icon" aria-hidden />
                          <p className="mb-0">Click to select or drag and drop</p>
                          <small>.xlsx or .xls</small>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {importFile && preview && (
              <>
                <div className="donezo-orders-panel donezo-upload-section mb-3">
                  <div className="donezo-upload-section__header donezo-upload-section__header--split">
                    <div className="d-flex align-items-center gap-2">
                      <i className="fas fa-calendar-alt" aria-hidden />
                      <h2 className="donezo-upload-section__title mb-0">Sessions by date</h2>
                    </div>
                    <span className="donezo-upload-section__meta">
                      {preview.totalRows} fish · {preview.newSessionCount} new ·{" "}
                      {preview.appendSessionCount} append
                    </span>
                  </div>
                  <div className="table-responsive">
                    <table className="table align-middle mb-0 donezo-orders-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Session name</th>
                          <th>Action</th>
                          <th className="text-end">Fish</th>
                          <th>Species</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(preview.sessions || []).map((s) => (
                          <tr key={s.dateIso}>
                            <td>
                              <span className="donezo-orders-pond__name">{s.dateLabel}</span>
                            </td>
                            <td>{s.name}</td>
                            <td>
                              {s.isAppend ? (
                                <span className="donezo-status-dot donezo-status-dot--river">
                                  <span className="donezo-status-dot__mark" aria-hidden />
                                  Append
                                </span>
                              ) : (
                                <span className="donezo-status-dot donezo-status-dot--natural">
                                  <span className="donezo-status-dot__mark" aria-hidden />
                                  Create
                                </span>
                              )}
                            </td>
                            <td className="text-end">{s.rowCount}</td>
                            <td className="donezo-orders-table__date">
                              {(s.speciesList || []).join(", ") || "—"}
                            </td>
                          </tr>
                        ))}
                        {!preview.sessions?.length && (
                          <tr>
                            <td colSpan={5} className="text-center donezo-orders-table__date py-3">
                              No valid sessions found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="donezo-orders-panel donezo-upload-section mb-3">
                  <div className="donezo-upload-section__header">
                    <i className="fas fa-file-excel" aria-hidden />
                    <h2 className="donezo-upload-section__title">{importFile.name}</h2>
                  </div>
                  {preview.totalRows > previewFishRows.length && (
                    <div className="donezo-upload-section__hint px-3 pt-2 mb-0">
                      Showing first {previewFishRows.length} of {preview.totalRows} fish rows.
                    </div>
                  )}
                  <div className="table-responsive">
                    <table className="table align-middle mb-0 donezo-orders-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Fish Type</th>
                          <th>Length ({lengthUnitName || "—"})</th>
                          <th>Weight ({weightUnitName || "—"})</th>
                          <th>Tag Number</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewFishRows.map((row, index) => (
                          <tr key={`${row.rowNumber}-${index}`}>
                            <td>{row.Date}</td>
                            <td>{row.FishTypeName}</td>
                            <td>{row.Length}</td>
                            <td>{row.Weight}</td>
                            <td>{row.TagNumber || "—"}</td>
                            <td>
                              <span
                                className={`donezo-status-dot donezo-status-dot--${
                                  row.Status === "Culled"
                                    ? "natural"
                                    : row.Status === "Released"
                                      ? "river"
                                      : "other"
                                }`}
                              >
                                <span className="donezo-status-dot__mark" aria-hidden />
                                {row.Status || "Not Set"}
                              </span>
                            </td>
                          </tr>
                        ))}
                        {!previewFishRows.length && (
                          <tr>
                            <td colSpan={6} className="text-center donezo-orders-table__date py-3">
                              No fish rows to preview
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {preview?.errors?.length > 0 && (
              <div className="donezo-upload-notice donezo-upload-notice--error mb-3">
                <div className="donezo-upload-notice__title">
                  <i className="fas fa-exclamation-triangle me-2" aria-hidden />
                  Validation errors
                </div>
                <ul className="donezo-upload-notice__list">
                  {preview.errors.slice(0, 20).map((error, index) => (
                    <li key={index}>
                      {error.rowNumber ? `Row ${error.rowNumber}: ` : ""}
                      {error.message}
                    </li>
                  ))}
                  {preview.errors.length > 20 && (
                    <li>…and {preview.errors.length - 20} more</li>
                  )}
                </ul>
              </div>
            )}

            <div className="d-flex justify-content-between flex-wrap gap-2 mt-2">
              <button
                type="button"
                className="donezo-orders-toolbar__ghost"
                disabled={isCommitting || isParsingFile}
                onClick={() => {
                  clearImportState();
                  setStep(1);
                }}
              >
                <i className="fa fa-arrow-left" aria-hidden />
                Back
              </button>
              <div className="d-flex flex-wrap gap-2">
                {importFile && (
                  <button
                    type="button"
                    className="donezo-orders-toolbar__ghost"
                    disabled={isCommitting || isParsingFile}
                    onClick={clearImportState}
                  >
                    Clear file
                  </button>
                )}
                <button
                  type="button"
                  className="donezo-orders-toolbar__primary"
                  disabled={!preview?.ok || isCommitting || isParsingFile || !importFile}
                  onClick={handleCommit}
                >
                  {isCommitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin me-1" aria-hidden />
                      Importing…
                    </>
                  ) : (
                    <>
                      <i className="fas fa-file-import me-1" aria-hidden />
                      Import data
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {step === 3 && commitResult && (
          <div className="donezo-orders-panel donezo-upload-section">
            <div className="donezo-upload-section__body donezo-upload-done">
              <div className="donezo-upload-done__hero">
                <span
                  className={`donezo-upload-done__icon${
                    commitResult.queued ? " is-queued" : " is-ok"
                  }`}
                  aria-hidden
                >
                  <i className={`fa ${commitResult.queued ? "fa-envelope" : "fa-check"}`} />
                </span>
                <div>
                  <h2 className="donezo-upload-done__title">
                    {commitResult.queued ? "Import queued" : "Import complete"}
                  </h2>
                  <p className="donezo-upload-done__text">{commitResult.message}</p>
                  {commitResult.queued && commitResult.notifyEmail && (
                    <p className="donezo-upload-done__text mt-2 mb-0">
                      Confirmation will be sent to <strong>{commitResult.notifyEmail}</strong> when
                      processing finishes.
                    </p>
                  )}
                </div>
              </div>

              {!commitResult.queued && (
                <ul className="donezo-upload-done__sessions">
                  {(commitResult.sessions || []).map((s) => (
                    <li key={s.pondDID}>
                      <Link to={`/samplingViaPondDetail/${s.pondDID}`}>
                        {s.name || s.date}
                      </Link>
                      <span>
                        {s.sampleCount} fish
                        {s.created ? " · new" : " · appended"}
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              {commitResult.queued && (
                <div className="donezo-upload-notice mb-4">
                  Processing continues in the background. You can leave this page or upload another
                  file — check your email for the result.
                  {commitResult.jobId != null && (
                    <div className="mt-1 opacity-75">Job #{commitResult.jobId}</div>
                  )}
                </div>
              )}

              <div className="d-flex flex-wrap gap-2">
                <button
                  type="button"
                  className="donezo-orders-toolbar__primary"
                  onClick={() => history.push(`/pondDetailViaPond/${pondId}`)}
                >
                  Back to sampling list
                </button>
                <button
                  type="button"
                  className="donezo-orders-toolbar__ghost"
                  onClick={() => {
                    setCommitResult(null);
                    clearImportState();
                    setStep(1);
                  }}
                >
                  Upload another file
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PondSamplingUpload;
